(() => {
  class UIAccordion {
    static defaults = {
      showArrow: true, // show/hide arrow
      trigger: "click", // 'click' | 'hover'
      singleOpen: false, // only one open per group
      hoverIntent: 80, // ms delay before opening on hover
      animate: true,
      duration: 220,
      easing: "cubic-bezier(.4,0,.2,1)",
    };

    constructor(root, opts = {}) {
      this.root = root;
      const ds = {
        showArrow: this._dsBool(root.dataset.accArrow, true),
        trigger: root.dataset.accTrigger || undefined,
        singleOpen: this._dsBool(root.dataset.accSingle, false),
      };
      this.opts = { ...UIAccordion.defaults, ...ds, ...opts };
      this.items = Array.from(root.querySelectorAll(".ui-accitem"));
      this.headers = this.items.map((i) =>
        i.querySelector("[data-acc-toggle]")
      );
      this.panels = this.items.map((i) => i.querySelector(".ui-accpanel"));
      this._idsN = 0;

      this._setupA11y();
      this._bind();
      this._applyStyleHooks();
    }

    _applyStyleHooks() {
      this.root.style.setProperty("--acc-speed", `${this.opts.duration}ms`);
      this.root.style.setProperty("--acc-ease", this.opts.easing);
      if (!this.opts.showArrow)
        this.root.setAttribute("data-acc-arrow", "false");
    }

    _setupA11y() {
      this.items.forEach((item, idx) => {
        const btn = item.querySelector("[data-acc-toggle]");
        const panel = item.querySelector(".ui-accpanel");

        const hid = `acc-h-${this._uid(idx)}`;
        const pid = `acc-p-${this._uid(idx)}`;
        btn.id = btn.id || hid;
        panel.id = panel.id || pid;

        btn.setAttribute("aria-controls", panel.id);
        btn.setAttribute("aria-expanded", "false");
        btn.setAttribute("role", "button");

        panel.setAttribute("role", "region");
        panel.setAttribute("aria-labelledby", btn.id);
        panel.hidden = true;
      });
    }

    _bind() {
      this.headers.forEach((btn, idx) => {
        if (this.opts.trigger === "hover") {
          let hoverTimer;
          btn.addEventListener("mouseenter", () => {
            hoverTimer = setTimeout(
              () => this.open(idx),
              this.opts.hoverIntent
            );
          });
          btn.addEventListener("mouseleave", () => {
            clearTimeout(hoverTimer);
          });
          // keep open while inside panel
          const panel = this.panels[idx];
          panel.addEventListener("mouseleave", () => this.close(idx));
        } else {
          btn.addEventListener("click", () => this.toggle(idx));
        }

        // keyboard support
        btn.addEventListener("keydown", (e) => this._onKeydown(e, idx));
      });
    }

    _onKeydown(e, idx) {
      const key = e.key;
      if (key === "Enter" || key === " ") {
        e.preventDefault();
        this.toggle(idx);
      } else if (key === "ArrowDown") {
        e.preventDefault();
        this._focusNext(idx);
      } else if (key === "ArrowUp") {
        e.preventDefault();
        this._focusPrev(idx);
      } else if (key === "Home") {
        e.preventDefault();
        this.headers[0]?.focus();
      } else if (key === "End") {
        e.preventDefault();
        this.headers[this.headers.length - 1]?.focus();
      }
    }

    _focusNext(idx) {
      (this.headers[idx + 1] || this.headers[0]).focus();
    }
    _focusPrev(idx) {
      (this.headers[idx - 1] || this.headers[this.headers.length - 1]).focus();
    }

    toggle(idx) {
      this.isOpen(idx) ? this.close(idx) : this.open(idx);
    }
    isOpen(idx) {
      return this.headers[idx].getAttribute("aria-expanded") === "true";
    }

    open(idx) {
      if (this.opts.singleOpen)
        this.items.forEach((_, i) => i !== idx && this.close(i));
      const btn = this.headers[idx],
        panel = this.panels[idx];
      if (this.isOpen(idx)) return;

      btn.setAttribute("aria-expanded", "true");
      panel.hidden = false;

      if (!this.opts.animate) return;
      this._animateHeight(panel, true);
    }

    close(idx) {
      const btn = this.headers[idx],
        panel = this.panels[idx];
      if (!this.isOpen(idx)) return;

      btn.setAttribute("aria-expanded", "false");

      if (!this.opts.animate) {
        panel.hidden = true;
        return;
      }
      this._animateHeight(panel, false, () => {
        panel.hidden = true;
      });
    }

    _animateHeight(panel, opening, onDone) {
      const inner = panel.querySelector(".ui-accpanel__inner");
      const start = opening ? 0 : inner.offsetHeight;
      const end = opening ? inner.offsetHeight : 0;

      panel.style.height = `${start}px`;
      panel.getBoundingClientRect(); // reflow
      panel.style.transition = `height ${this.opts.duration}ms ${this.opts.easing}`;
      panel.style.height = `${end}px`;

      const cleanup = () => {
        panel.style.height = "";
        panel.style.transition = "";
        panel.removeEventListener("transitionend", cleanup);
        onDone && onDone();
      };
      panel.addEventListener("transitionend", cleanup);
    }

    _uid(seed) {
      return `${(this._idsN++).toString(36)}-${seed}`;
    }
    _dsBool(v, fallback = false) {
      if (v === undefined) return fallback;
      return String(v).toLowerCase() !== "false";
    }

    // Public API
    closeAll() {
      this.items.forEach((_, i) => this.close(i));
    }
    openFirst() {
      if (this.items.length) this.open(0);
    }

    // static helpers
    static mountAll(opts = {}) {
      const nodes = document.querySelectorAll(".ui-accordion");
      return Array.from(nodes).map((n) => new UIAccordion(n, opts));
    }
  }

  // Auto-mount (customize defaults here if you want a global policy)
  window.UIAccordion = UIAccordion;
  UIAccordion.mountAll({
    // showArrow: true,
    // trigger: 'click',    // set 'hover' to enable hover open
    // singleOpen: true
  });
})();
